import { useEffect, useState } from "react"
import { DateTime } from "luxon"
import { ReadMonitorRunResultParams, useReadMonitorRunResultGroupedByMonitorIdQuery } from "services/monitorRunResult"
import { compareLuxonDates } from "shared/lib/compareLuxonDates"
import { ChartPagination, ChartPaginationModel } from "../../presentationals/ChartPagination/ChartPagination"
import { Box, SxProps } from "@mui/material"
import { ChartCompetitorList } from "../../presentationals/ChartCompetitorList"
import { ChartDatePanel } from "../../presentationals/ChartDatePanel"
import { ButtonStyle, ChartDateMode, DateTimePeriod } from "../../presentationals/ChartDatePanel/ChartDatePanel"
import { median } from "shared/lib/median"
import { useReadMonitorByIdQuery } from "services/monitor"

interface ChartCompetitorListContainerProps {
    monitorId?: number
}

export const ChartCompetitorListContainer = (props: ChartCompetitorListContainerProps): JSX.Element => {
    const {
        monitorId
    } = props

    const [readMonitorRunResultByMonitorIdQueryParams, setReadMonitorRunResultByMonitorIdQueryParams] = useState<ReadMonitorRunResultParams>({
        monitorId: monitorId,
    });

    useEffect(() => {
        setReadMonitorRunResultByMonitorIdQueryParams(
            {
                monitorId: monitorId,
            }
        )
    }, [monitorId])

    const [readMonitorRunResultByMonitorIdQueryOptions, setReadMonitorRunResultByMonitorIdQueryOptions] = useState<any>({
        skip: monitorId === undefined,
        refetchOnMountOrArgChange: true,
        pollingInterval: 5000,
    })

    const {
        data: readMonitorRunResultGroupedByMonitorIdData,
        isLoading: readMonitorRunResultGroupedByMonitorIdsLoading,
        isFetching: readMonitorRunResultGroupedByMonitorIdIsFetching,
    } = useReadMonitorRunResultGroupedByMonitorIdQuery(
        readMonitorRunResultByMonitorIdQueryParams,
        readMonitorRunResultByMonitorIdQueryOptions
        );

    const [readMonitorByIdQueryParams, setReadMonitorByIdQueryParams] = useState<any>(undefined);
    const [readMonitorByIdOptions, setReadMonitorByIdOptions] = useState<any>(
        {
            skip: true
        }
    );

    const {
        data: readMonitorByIdQueryData,
        isLoading: readMonitorByIdQueryIsLoading
    } = useReadMonitorByIdQuery(readMonitorByIdQueryParams, readMonitorByIdOptions);

    useEffect(() => {
        setReadMonitorByIdQueryParams((prevValue: any) => {
            return {
                ...prevValue,
                monitorId: monitorId
            }
        });

        setReadMonitorByIdOptions((prevValue: any) => {
            return {
                    ...prevValue,
                    skip: monitorId === undefined,
                };
        }) 
    }, [
        monitorId
    ])

    useEffect(() => {
        setReadMonitorRunResultByMonitorIdQueryParams(
            {
                monitorId: monitorId,
            }
        )

        setReadMonitorRunResultByMonitorIdQueryOptions({
            skip: monitorId === undefined,
            refetchOnMountOrArgChange: true,
            pollingInterval: 5000,
        })

    }, [
        monitorId
    ])

    const [data, setData] = useState<any>();
    const [startDateOfData, setStartDateOfData] = useState<DateTime | undefined>(undefined);
    const [chartDateMode, setChartDateMode] = useState<ChartDateMode | undefined>(undefined);
    const [chartDateTimePeriod, setChartDateTimePeriod] = useState<DateTimePeriod | undefined>(undefined);
    const [toCompetitorsGrow, setToCompetitorsGrow] = useState<number>();
    const [toCompetitorsGrowPercent, setToCompetitorsGrowPercent] = useState<number>();
    const [chartPagination, setChartPagination] = useState<ChartPaginationModel | undefined>();
    const [chartCompetitorListData, setChartCompetitorListData] = useState<Object | undefined>();
    const [competitorsMarketDirection, setCompetitorsMarketDirection] = useState<number | undefined>(undefined)
    const [competitorsMarketDirectionGrow, setCompetitorsMarketDirectionGrow] = useState<number | undefined>(undefined)
    const [competitorsDirectionGrowPercent, setCompetitorsMarketDirectionGrowPercent] = useState<number | undefined>(undefined)
    const [chartCompetitorListOptions, setCompetitorListOptions] = useState<any>({
        elements: {
            point: {
                radius: 5,
                hoverRadius: 7,
                hitRadius: 16
            }
        },
        responsive: true,
        scales: {
            y: {

                ticks: {
                    color: "#000000",
                    precision: 0,
                    beginAtZero: true
                }
            },
            x: {

                type: "timeseries",
                stacked: true,
                ticks: {
                    color: "#000000",
                    maxTicksLimit: 6
                },
                time: {
                    displayFormats: {
                        day: 'MM/dd/yyyy',
                        hour: 'MM/dd/yyyy',
                        minute: 'MM/dd/yyyy',
                    }
                },
                adapters: {
                    date: {
                        zone: 'utc'
                    }
                }
            }
        },
        plugins: {
            title: {
                display: true,
                text: 'Competitors',
                font: {
                    size: 16
                }
            },
            legend: false,
            annotation: {
                annotations: {
                    label1: {
                        display: (obj: any, args: any, options: any) => {
                            return !obj.chart.data?.datasets.some((dataset: any) => dataset.data.length > 0)
                        },
                        type: 'label',
                        position: 'center',
                        backgroundColor: 'rgba(0,0,0, 0)',
                        content: ['No data available'],
                        font: {
                            size: 36,
                            weight: 600
                        }
                    }
                }
            }
        }
    });

    useEffect(() => {
        if (readMonitorByIdQueryData === undefined) {
            return
        }

        if (readMonitorByIdQueryData.customPeriods && readMonitorByIdQueryData.customPeriods.length > 0) {
            const startDate = DateTime.fromISO(readMonitorByIdQueryData.customPeriods[0].startDateTimeUtc, { zone: "UTC" }).startOf('day')
            const endDate = DateTime.fromISO(readMonitorByIdQueryData.customPeriods[0].endDateTimeUtc, { zone: "UTC" }).endOf('day')


            setChartDateTimePeriod((prevState: DateTimePeriod | undefined): any => {
                setChartDateTimePeriod({
                    from: startDate,
                    to: endDate
                })
            })

            setChartDateMode('Custom')

            return
        }

        setChartDateTimePeriod((prevState: DateTimePeriod | undefined): any => {
            setChartDateTimePeriod({
                from: DateTime.now().startOf('day'),
                to: DateTime.now().endOf('day').plus({ month: 1 })
            })
        })

        setChartDateMode('1M')
    },
        [
            readMonitorByIdQueryData
        ]
    )

    useEffect(() => {
        if (readMonitorRunResultGroupedByMonitorIdData === undefined) {
            setData(undefined)

            return
        }

        const transformedData: any[] = readMonitorRunResultGroupedByMonitorIdData.list
            .map((resultItem: any, index: number, list: any) => {
                const checkinUtc = DateTime.fromISO(resultItem?.checkinUtc, { setZone: true });

                return {
                    checkinUtc: checkinUtc,
                    competitors: resultItem.competitors
                }
            })
            .sort(compareLuxonDates)

        let trData = transformedData;

        if (chartDateTimePeriod !== undefined) {
            const fromDt = DateTime.fromObject({
                year: chartDateTimePeriod.from.year,
                month: chartDateTimePeriod.from.month,
                day: chartDateTimePeriod.from.day
            }, { zone: "UTC" })

            const toDt = DateTime.fromObject({
                year: chartDateTimePeriod.to.year,
                month: chartDateTimePeriod.to.month,
                day: chartDateTimePeriod.to.day,
                hour: 23,
                minute: 59,
                second: 59,
                millisecond: 999

            }, { zone: "UTC" })

            trData = transformedData.filter((row: any) => {
                return row.checkinUtc >= fromDt && row.checkinUtc <= toDt
            })
        }

        setData(trData)
    }, [readMonitorRunResultGroupedByMonitorIdData, chartDateTimePeriod]);

    useEffect(() => {
        function cleanState() {        
            setToCompetitorsGrow(undefined)
            setToCompetitorsGrowPercent(undefined)
            setCompetitorsMarketDirection(undefined)
            setCompetitorsMarketDirectionGrow(undefined)
            setCompetitorsMarketDirectionGrowPercent(undefined)
            setChartCompetitorListData({
                datasets: [],
            })
            setStartDateOfData(undefined)
        }

        function handleChartCompetitorsOptions() {
            setCompetitorListOptions((prevValue: any) => {
                const scales = {
                    ...prevValue.scales
                }

                let maxValue = Math.max.apply(null, data.map((o: any) => { return o.competitors; }));
                let minValue = Math.min.apply(null, data.map((o: any) => { return o.competitors; }));

                scales.y.min = minValue - 10;
                scales.y.max = maxValue + 10;

                return ({
                    ...prevValue,
                    scales
                });
            })
        }

        function handleStateAndDatasets() {
            if (data === undefined || data.length <= 0) {
                return
            }

            const datasets = []
            const startItem = data.find((row: any) => row.competitors !== null)
            const endItem = data.findLast((row: any) => row.competitors !== null)

            if (startItem === undefined || endItem === undefined) {
                return
            }

            const startCompetitors = startItem.competitors
            const endCompetitors = endItem.competitors
            const competitorsGrow = endCompetitors - startCompetitors
            let percentGrow = undefined

            if (startCompetitors === 0) {
                percentGrow = (competitorsGrow / 1) * 100
            } else {
                percentGrow = (competitorsGrow / startCompetitors) * 100
            }

            if (competitorsGrow >= 0) {
                percentGrow = Math.abs(percentGrow)
            }

                
            setToCompetitorsGrow(competitorsGrow)
            setToCompetitorsGrowPercent(percentGrow)

            const arr = data
                .filter((row: any) => row.competitors !== null)
                .map((o: any) => { return (o.competitors) })

            let diffCompetitorsStartMedianCompetitors = undefined
            const medianResult = median(arr)
            if (medianResult !== undefined) {
                diffCompetitorsStartMedianCompetitors = medianResult - startCompetitors
                let percentCompetitorsMarketGrow: number | undefined = undefined

                if (startCompetitors === 0) {
                    percentCompetitorsMarketGrow = (diffCompetitorsStartMedianCompetitors / 1) * 100
                } else {
                    percentCompetitorsMarketGrow = (diffCompetitorsStartMedianCompetitors / startCompetitors) * 100
                }

                if (diffCompetitorsStartMedianCompetitors >= 0) {
                    percentCompetitorsMarketGrow = Math.abs(percentCompetitorsMarketGrow)
                }

                    
                setCompetitorsMarketDirection(medianResult)
                setCompetitorsMarketDirectionGrow(diffCompetitorsStartMedianCompetitors)
                setCompetitorsMarketDirectionGrowPercent(percentCompetitorsMarketGrow)
                const borderColor = diffCompetitorsStartMedianCompetitors < 0 ? '#FE0000' : '#4caf50'

                datasets.push(
                    {
                        fill: 'start',
                        type: 'line' as const,
                        label: 'Market direction',
                        data: [
                            {
                                competitorsMarketDirectionGrow: startItem.competitors,
                                checkinUtc: startItem.checkinUtc
                            },
                            {
                                competitorsMarketDirectionGrow: medianResult,
                                checkinUtc: endItem.checkinUtc
                            }
                        ],
                        parsing: {
                            yAxisKey: 'competitorsMarketDirectionGrow',
                            xAxisKey: 'checkinUtc',
                        },
                        borderColor: borderColor,
                        backgroundColor: () => (`rgba(254, 0, 0, 0)`)
                    }
                )
            }

            const negativeDirection = diffCompetitorsStartMedianCompetitors !== undefined && diffCompetitorsStartMedianCompetitors < 0
            const mainGrapBorderColor = negativeDirection ? '#FE0000' : '#4caf50'

            datasets.push({
                fill: 'start',
                type: 'line' as const,
                label: 'Competitors',
                data: data,
                parsing: {
                    yAxisKey: 'competitors',
                    xAxisKey: 'checkinUtc',
                },
                borderColor: mainGrapBorderColor,
                backgroundColor: function (context: any) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;

                    if (!chartArea) {
                        return;
                    }

                    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

                    if (negativeDirection) {
                        gradient.addColorStop(0, 'rgba(254, 0, 0, 0.2)');
                        gradient.addColorStop(1, 'rgba(254, 0, 0, 0.05)');
                    } else {
                        gradient.addColorStop(0, 'rgba(76, 176, 80, 0.2)');
                        gradient.addColorStop(1, 'rgba(76, 176, 80, 0.05)');
                    }

                    return gradient;
                },
            })
            
            
            setChartCompetitorListData({
                datasets
            })

            setStartDateOfData(undefined)
        }

        cleanState()

        if (data === undefined) {
            return
        }

        handleChartCompetitorsOptions()
        handleStateAndDatasets()
    }, [
        data
    ])

    const chartDatePanelButtonStyle: ButtonStyle = {
        color: {
            default: '#C0C0C0',
            hover: '#FC8835',
            selected: '#FC8835'
        },
        borderColor: {
            default: '#C0C0C0',
            hover: '#FC8835',
            selected: '#FC8835'
        }
    }

    const handleModeChange = (mode: ChartDateMode) => {
        setChartDateMode(mode)
    }

    const handleDateTimePeriodChange = (period: DateTimePeriod) => {
        setChartDateTimePeriod(period)
    }

    const namePropStyle = {
        fontWeight: '700',
        fontSize: '20px',
        margin: '0 0 10px 0'
    }

    const propertyListContainer = {
        padding: '20px 0px',
        gridTemplateColumns: 'max-content max-content max-content',
        display: 'grid',

    }

    const valuePropStyle = {
        margin: '0 0 10px 20px',
        fontSize: '20px',
        '&.CellValue__Root_isGrowing': {
            color: `#4CB050`
        },
        '&.CellValue__Root_isFalling': {
            color: `#FE0000`
        }
    }

    const renderValue = (value: any) => {
        if (value === undefined) {
            return (<Box></Box>)
        }

        let classes = `CellValue__Root_isUnchanged`
        let handledValue = `${value.toFixed(0)}`

        if (value < 0) {
            //classes = 'CellValue__Root_isFalling'
            handledValue = `-${Math.abs(value).toFixed(0)}`
        } else if (value > 0) {
            //classes = 'CellValue__Root_isGrowing'
            handledValue = `+${Math.abs(value).toFixed(0)}`
        }

        return (
            <Box className={classes}
                sx={valuePropStyle}>
                {handledValue}
            </Box>
        )
    }

    const renderValuePercent = (value: any) => {
        if (value === undefined) {
            return (<Box></Box>)
        }

        let classes = `CellValue__Root_isUnchanged`
        let handledValue = `${value.toFixed(2)}%`

        if (value < 0) {
            classes = 'CellValue__Root_isFalling'
            handledValue = `${value.toFixed(2)}%`
        } else if (value > 0) {
            classes = 'CellValue__Root_isGrowing'
            handledValue = `+${value.toFixed(2)}%`
        }

        return (
            <Box className={classes}
                sx={valuePropStyle}>
                {handledValue}
            </Box>
        )
    }

    const datesContainer: SxProps = {
        padding: '0 0',
        gridTemplateColumns: 'max-content 1fr',
        display: 'grid',
        fontSize: '20px'
    }

    const propertyTitle: SxProps = {
        fontWeight: '700',
        fontSize: '20px',
        margin: '0 10px 0 0'
    }

    const toDateFormat = (date: DateTime) => {
        return date.toFormat('M/dd/yyyy')
    }

    return (
        <Box>
            <Box sx={datesContainer}>
                <Box
                    sx={propertyTitle}
                >
                    Start:
                </Box>
                <Box>
                    {chartDateTimePeriod?.from ? toDateFormat(chartDateTimePeriod.from) : ''}
                </Box>
                <Box
                    sx={propertyTitle}
                >
                    End:
                </Box>
                <Box>
                    {chartDateTimePeriod?.to ? toDateFormat(chartDateTimePeriod.to) : ''}
                </Box>
            </Box>

            <ChartCompetitorList
                data={chartCompetitorListData}
                options={chartCompetitorListOptions}
                loading={chartCompetitorListData === undefined}
            />

            <ChartDatePanel
                buttonStyle={chartDatePanelButtonStyle}
                mode={chartDateMode}
                onModeChange={handleModeChange}
                onDateTimePeriodChange={handleDateTimePeriodChange}
                dateTimePeriod={chartDateTimePeriod}
            />

            <Box sx={propertyListContainer}>
                <Box sx={namePropStyle}>
                    Competitors:
                </Box>
                {
                    renderValue(toCompetitorsGrow)
                }
                {
                    renderValuePercent(toCompetitorsGrowPercent)
                }
                <Box sx={namePropStyle}>
                    Market direction:
                </Box>
                {
                    renderValue(competitorsMarketDirectionGrow)
                }
                {
                    renderValuePercent(competitorsDirectionGrowPercent)
                }
            </Box>
        </Box>
    )
}